<template>
  <v-container class="photos">
    <ViewHeader :title="$t('message.photos.list.title')">
      <ui-button
        v-if="canAddAndDeletePhotos"
        :to="{ name: 'photos-add' }"
        color="primary"
        icon="icon-plus"
      >
        {{ $t("message.photos.list.addNew") }}
      </ui-button>
    </ViewHeader>
    <v-card class="photos__body">
      <SectionLoader :status="initLoading" :opacity="1" />
      <template v-if="hasAnyPhotos && !initLoading">
        <Pagination
          v-bind="paginationProps"
          :show-options="totalCount > 0"
          show-if-empty
          @change="onPaginationChange"
        >
          <div class="d-flex ml-3">
            <DateRangePicker
              v-model="date"
              :label="$t('message.photos.list.sendDate').toString()"
              @change="onPaginationChange({ page: 1 })"
            />
          </div>
        </Pagination>

        <div class="photos__list">
          <SectionLoader :status="loading" />
          <PhotosEmpty
            v-if="totalCount === 0 && !loading"
            :message="$t('message.photos.list.notFound').toString()"
            :show-button="canAddAndDeletePhotos"
          />
          <template v-else>
            <PhotoListItem
              v-for="item in items"
              :item="item"
              :key="item.id"
              :show-delete="canAddAndDeletePhotos"
              :show-store-locator="hasStoreLocator"
              @delete="onDelete(item.id, item.created_at, item.sent_count)"
              @sl-visible="onToggleVisibility(item.id, $event)"
            />
          </template>
        </div>
        <Pagination
          v-bind="paginationProps"
          :show-options="false"
          show-if-empty
          @change="onPaginationChange"
        />
      </template>
      <PhotosEmpty
        v-else
        :show-button="canAddAndDeletePhotos"
        :main-text="optionalEmptyPhotosMessage"
      />
    </v-card>
  </v-container>
</template>

<script>
import ViewHeader from "@/components/ui/ViewHeader.vue";
import serviceHandler from "@/helpers/handlers/service";
import photosService from "@/services/photos";
import Pagination from "@/components/List/Pagination";
import { dateRangeFilterMixin } from "@/helpers/mixins/date-range-filter";
import DateRangePicker from "@/components/ui/DateRangePicker";
import PhotoListItem from "@/components/Photos/PhotoListItem";
import { DialogHelper } from "@/helpers/dialog-helper";
import PhotoDeleteDialog from "@/components/Photos/PhotoDeleteDialog";
import SectionLoader from "@/components/Loader/SectionLoader";
import PhotosEmpty from "@/components/Photos/PhotosEmpty";
import { permissionsInfo } from "@/helpers/mixins/permissions-info";
import { mapGetters } from "vuex";
import { gtmEngagement as gtmEngagementMixin } from "@/helpers/mixins/gtm-engagement";

export default {
  name: "Photos",
  mixins: [dateRangeFilterMixin, permissionsInfo, gtmEngagementMixin],
  components: {
    ViewHeader,
    Pagination,
    DateRangePicker,
    PhotoListItem,
    SectionLoader,
    PhotosEmpty
  },
  data() {
    return {
      initLoading: false,
      loading: false,
      items: null,
      totalCount: 0,
      page: 1,
      perPageOptions: [10, 25, 50, 100],
      perPage: 10,
      date: this.getStartDate(),
      hasAnyPhotos: false
    };
  },
  computed: {
    ...mapGetters({
      hasStoreLocator: "company/hasStoreLocator"
    }),
    optionalEmptyPhotosMessage() {
      return !this.hasAnyPhotos && !this.canAddAndDeletePhotos
        ? this.$t("message.photos.list.noPhotos")
        : undefined;
    },
    queryParams() {
      return {
        page: this.page,
        page_size: this.perPage,
        date_from: this.date[0],
        date_to: this.date[1]
      };
    },
    paginationProps() {
      return {
        page: this.page,
        perPage: this.perPage,
        options: this.perPageOptions,
        totalCount: this.totalCount,
        disabled: this.loading
      };
    }
  },
  watch: {
    queryParams: {
      handler() {
        this.fetchPage();
      }
    }
  },
  async created() {
    this.initLoading = true;
    await this.checkHasAnyPhotos();
    await this.fetchPage();
    this.initLoading = false;
  },
  mounted() {
    this.mixin__setGtmEngagementTimeouts();
  },
  methods: {
    async fetchPage() {
      this.loading = true;
      const { count, results } = await serviceHandler(
        photosService.getPhotos(this.queryParams)
      );
      this.totalCount = count;
      this.items = results;
      this.loading = false;
    },

    async checkHasAnyPhotos() {
      const ids = await serviceHandler(
        photosService.getPhotos({ only_ids: true })
      );
      this.hasAnyPhotos = !!ids.length;
    },

    async onPaginationChange({ page, perPage }) {
      this.page = page || this.page;
      this.perPage = perPage || this.perPage;
    },

    async onToggleVisibility(id, visible) {
      await serviceHandler(
        photosService.setStoreLocatorVisibility(id, visible)
      );
    },

    onDelete(photoId, publishDate, numLocations) {
      DialogHelper.showDialog(
        PhotoDeleteDialog,
        { photoId, publishDate, numLocations },
        [
          {
            event: "confirm",
            callback: () => {
              this.checkHasAnyPhotos();
              this.fetchPage();
            }
          }
        ]
      );
    }
  }
};
</script>

<style lang="scss" scoped>
.photos {
  &__body {
    position: relative;
    padding: $spacing * 4;
  }

  &__list {
    min-height: 220px;
    position: relative;
  }
}
</style>
